import React, { useContext, useState, useRef, useEffect } from "react";
import { ShopContext } from "../../context/ShopContext";
import { CartItem } from "./CartItem";
import Footer from "../../components/Footer";
import { Link } from "react-router-dom";
import DropdownMenu from "../../components/DropdownMenu";
import { useNavigate } from "react-router-dom";
import SEO from "../../context/SEO";
import styles from "./Cart.module.scss";
// ✅ ADDED: Import cookie consent hook
import { useCookieConsent } from "../../context/CookieConsentContext";

const Cart = () => {
  let navigate = useNavigate();
  const { cartItems, getTotalCartAmount, products } = useContext(ShopContext);
  const [showElements, setShowElements] = useState("full-opacity");
  const totalAmount = getTotalCartAmount();
  const [isLoading, setLoading] = useState(false);
  const carouselRef = useRef(null);
  // ✅ ADDED: Get cookie consent state
  const { cookieConsent } = useCookieConsent();

  console.log("Cart items:", cartItems);
  const cartItemsArray = Object.entries(cartItems).flatMap(
    ([itemId, variations]) => {
      const product = products.find((product) => product.id === itemId);
      if (!product) return [];

      return Object.entries(variations).map(([variationIndex, detail]) => ({
        ...product,
        variationIndex: parseInt(variationIndex),
        quantity: detail.quantity,
        variation: product.variations[variationIndex],
      }));
    },
  );

  const handleGoToShop = () => {
    navigate("/shop");
  };

  // Shopify cart item format
  const getCartArray = (cartItems) => {
    const cartArray = [];
    for (const itemId in cartItems) {
      const product = products.find((product) => product.id === itemId);
      if (!product) {
        console.warn(`Product with ID ${itemId} not found in products array`);
        continue;
      }
      for (const variation in cartItems[itemId]) {
        const variationData = product.variations[variation];
        if (!variationData || !variationData.stripeId) {
          console.warn(
            `Invalid variation ${variation} for product ${itemId}: stripeId is missing`,
          );
          continue;
        }
        const variantId = `gid://shopify/ProductVariant/${variationData.stripeId}`;
        if (!/gid:\/\/shopify\/ProductVariant\/\d+/.test(variantId)) {
          console.warn(
            `Invalid stripeId format for product ${itemId}, variation ${variation}: ${variationData.stripeId}`,
          );
          continue;
        }
        cartArray.push({
          variantId,
          quantity: cartItems[itemId][variation].quantity,
        });
      }
    }
    console.log("Generated cartArray:", cartArray);
    return cartArray;
  };

  const lineItems = getCartArray(cartItems);

  const Checkout = async () => {
    setLoading(true);
    try {
      const shopifyDomain = process.env.REACT_APP_SHOPIFY_DOMAIN;
      const storefrontAccessToken =
        process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

      if (!shopifyDomain || !storefrontAccessToken) {
        throw new Error(
          "Shopify domain or Storefront API token is missing. Please check your environment variables.",
        );
      }

      const graphqlEndpoint = `https://${shopifyDomain}/api/2025-10/graphql.json`;

      const query = `
        mutation cartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        input: {
          lines: lineItems.map((item) => ({
            merchandiseId: item.variantId,
            quantity: item.quantity,
          })),
        },
      };

      const response = await fetch(graphqlEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
        },
        body: JSON.stringify({ query, variables }),
      });

      console.log("Response status:", response.status);
      const responseBody = await response.json();
      console.log("Full API response:", responseBody);

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${JSON.stringify(responseBody)}`,
        );
      }

      const { data, errors } = responseBody;

      if (errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(errors)}`);
      }

      if (!data || !data.cartCreate) {
        throw new Error(
          `Invalid response: cartCreate is undefined. Full response: ${JSON.stringify(responseBody)}`,
        );
      }

      if (data.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
        throw new Error(data.cartCreate.userErrors[0].message);
      }

      const checkoutUrl = data.cartCreate.cart.checkoutUrl;

      // Hand off to Shopify checkout while preserving attribution.
      //
      // - gclid / gbraid / wbraid / utm_* are forwarded unconditionally:
      //   they are attribution markers, not personal data, and the Shopify
      //   Google & YouTube channel app needs them on the destination URL to
      //   credit the click that drove the order.
      // - The _gl linker (Google cross-domain) is only added when the user
      //   has granted consent. _gl encodes the GA client ID / Ads client ID
      //   and so requires ad_storage/analytics_storage = granted.
      // - We never block on gtag: a 1.5s timeout guarantees the redirect
      //   always happens even if gtag is blocked, slow, or missing.

      const attrKeys = [
        "gclid",
        "gbraid",
        "wbraid",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
      ];
      const liveParams = new URLSearchParams(window.location.search);
      const attrs = {};
      attrKeys.forEach((k) => {
        const v = liveParams.get(k) || sessionStorage.getItem("attr_" + k);
        if (v) attrs[k] = v;
      });

      const buildFinalUrlForRedirect = () => {
        const url = new URL(checkoutUrl);
        Object.entries(attrs).forEach(([k, v]) => url.searchParams.set(k, v));
        return url.toString();
      };

      const goViaRedirect = () => {
        window.location.assign(buildFinalUrlForRedirect());
      };

      const goViaDecoratedForm = () => {
        // Cross-domain hand-off via form submit. gtag.js with
        // decorate_forms: true (set in the linker config in
        // public/index.html) intercepts the form's submit event and
        // appends the _gl linker token to the action URL — the
        // officially documented approach for programmatic cross-domain
        // redirects in GA4.
        //
        // Two quirks to mind:
        // 1. form.submit() called from JS does NOT fire the 'submit'
        //    event that gtag listens to. We have to click an actual
        //    submit button (or call form.requestSubmit()) for gtag to
        //    intercept. We use a hidden submit button for browser
        //    compatibility.
        // 2. A GET form replaces the action URL's query string with the
        //    serialized form data. So we must put ALL params — Shopify's
        //    own _r/edge_redirect/etc. AND our gclid/utm_* — into hidden
        //    fields, otherwise Shopify's checkout token params get lost.
        const url = new URL(checkoutUrl);
        const form = document.createElement("form");
        form.method = "GET";
        form.action = url.origin + url.pathname;
        form.style.display = "none";

        const addField = (name, value) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };

        // Preserve Shopify's own query params (_r, auto_redirect, etc.)
        url.searchParams.forEach((v, k) => addField(k, v));
        // Add our preserved inbound attribution
        Object.entries(attrs).forEach(([k, v]) => addField(k, v));

        const submitBtn = document.createElement("button");
        submitBtn.type = "submit";
        form.appendChild(submitBtn);

        document.body.appendChild(form);
        submitBtn.click();
      };

      if (cookieConsent === true && typeof window.gtag === "function") {
        goViaDecoratedForm();
      } else {
        // No consent (or gtag missing): direct redirect with gclid/utm
        // preserved. The Shopify Google & YouTube channel app reads
        // gclid from the URL and attributes the purchase conversion,
        // so Ads attribution still works without _gl.
        goViaRedirect();
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      alert(`Checkout failed: ${error.message}`);
    }
    setLoading(false);
  };

  const vh = window.innerHeight * 0.01;

  const handleScroll = () => {
    const currentScrollPos = window.scrollY;

    if (
      currentScrollPos > -20 * vh &&
      currentScrollPos < 2 * vh &&
      showElements === "full-opacity"
    ) {
    } else if (currentScrollPos > -20 * vh && currentScrollPos < 2 * vh) {
      setShowElements("fade-in-up");
    } else {
      setShowElements("fade-out-up");
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showElements]);

  return (
    <div className={styles.cartPage}>
      <SEO title="Cork Matters - Cart" />
      <DropdownMenu showElements={showElements} />
      <div className={styles.logoMenuSpacer} />
      <div className={styles.cart}>
        {cartItemsArray.length > 0 ? (
          <>
            <div>
              <div className={styles.cartTotalText}>
                Your cart total is €{totalAmount.toFixed(2)}.
              </div>
            </div>
            <div className={styles.cartLayout}>
              <div className={styles.cartItems}>
                {cartItemsArray.map((item) => (
                  <CartItem
                    key={`${item.id}-${item.variationIndex}`}
                    data={item}
                  />
                ))}
              </div>
              <div className={styles.cartSummary}>
                <div className={styles.cartSummaryRow}>
                  <span>Subtotal (excl. tax)</span>
                  <span>€{(totalAmount * 0.77).toFixed(2)}</span>
                </div>
                <div className={styles.cartSummaryRow}>
                  <span>Tax</span>
                  <span>€{(totalAmount * 0.23).toFixed(2)}</span>
                </div>
                <div className={styles.cartSummaryRow}>
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div
                  className={`${styles.cartSummaryRow} ${styles.cartSummaryTotal}`}
                >
                  <span>Total</span>
                  <span>€{totalAmount.toFixed(2)}</span>
                </div>
                <button className={styles.cartSummaryBuy} onClick={Checkout}>
                  {isLoading ? "Loading..." : "Buy"}
                </button>
                <button
                  className={styles.cartSummaryContinue}
                  onClick={handleGoToShop}
                >
                  Continue shopping
                </button>
              </div>
            </div>
            <div className={styles.relatedProductsMenubar}>
              <div
                className={`${styles.fontFamilyNoah} ${styles.relatedProductsCart}`}
              >
                It may interest you
              </div>
            </div>
            <div className={styles.productCarousel} ref={carouselRef}>
              {products.slice(2, 6).map((otherProduct) => (
                <Link
                  to={`/product/${otherProduct.id}`}
                  key={otherProduct.id}
                  className={styles.carouselItem}
                >
                  <img
                    src={otherProduct.variations[0].coverPhoto}
                    alt={otherProduct.name}
                  />
                  <div className={styles.carouselItemName}>
                    {otherProduct.family
                      ? otherProduct.family
                      : otherProduct.name}{" "}
                    {otherProduct.family ? otherProduct.size : ""}
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={styles.fontFamilyNoah}>
              Your Shopping Cart is Empty
            </div>
            <div>
              <Link to="/products/all" className={styles.cartShopLink}>
                Visit Shop
              </Link>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
